// FinalizarCompra.js — Página única "Finalizando a Compra" (Fluxo de Compra do Figma).
//
// Pagamento: checkout da InfinitePay. O backend cria o pedido, pede um link de
// cobrança e o cliente é levado para a página de pagamento deles (PIX ou cartão),
// voltando pelo redirect_url. A confirmação chega sozinha pelo webhook.
// Pedidos abaixo de R$ 1,00 (mínimo da InfinitePay) caem no PIX da Insigne com
// QR na nossa própria página e confirmação manual pelo painel.
//
// Endereço: complemento é obrigatório para pedidos com livro físico (o backend
// recusa o pedido sem ele).
//
// Pedido duplicado: esta tela criou os pedidos #511, #512 e #515 a partir de um
// carrinho só. Três defesas convivem aqui, e nenhuma delas cobre o que a outra
// cobre — a trava síncrona (`enviando`) pega o segundo clique, a chave de
// idempotência pega a segunda REQUISIÇÃO, e o estado `inconclusivo` pega o caso
// em que o navegador não sabe se houve pedido. Ver src/utils/idempotencia.js.
import React, { useContext, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { message } from "../../utils/toast";
import TrilhaCompra, { ETAPAS_COMPRA } from "../TrilhaCompra";
import BotaoParticulas from "../../ui/BotaoParticulas";
import { CarrinhoContext } from "../../../services/context/CarrinhoContext";
import { useAuth } from "../../../services/context/AuthContext";
import useProfileData from "../../../hooks/userProfileData";
import useInsigneCoins from "../../../hooks/useInsigneCoins";
import LoginOrCreateAccount from "../../login/LoginOrCreateAccount";
import { campoCls } from "../../ui/Formulario";
import { chaveParaCarrinho, descartarChave } from "../../../utils/idempotencia";
import { linkSocorroWhatsApp } from "../../../utils/socorroCheckout";
import {
  fmtBRL, fmtPreco, autorDe, capaUrl, previsaoEntrega,
} from "../../compras/comprasUtils";
import { COIN_VALOR_CENTAVOS } from "../../../config/coins";
import useConfirmarGasto, { Coins } from "../../ui/ConfirmarGasto";

const API_URL = process.env.REACT_APP_API;
const main = process.env.REACT_APP_MAIN;

// ===== OS DOIS ÚNICOS BOTÕES DESTA TELA =====
//
// Sólido para a ação que o contexto existe para receber (salvar o endereço, ir
// pagar); secundário para o que volta atrás ou é opcional (cancelar, aplicar
// cupom, abrir o formulário de endereço). Eram quatro receitas escritas à mão,
// e a mesma coluna mostrava três alturas de botão: "Salvar" com `px-5`, que no
// Bootstrap é 3rem e quase o dobro do que a classe sugere, ao lado de "Aplicar"
// com `px-4`. Botão que muda de forma a cada linha faz hesitar justamente na
// tela em que se decide pagar.
//
// `px-6` e `py-2.5` entregam o valor do Tailwind DE VERDADE: a escala 0–5 do
// Bootstrap vem com !important e sequestra `px-4`, `px-5` e `py-3` (ver "A
// ESCALA DE ESPAÇAMENTO DESTE PROJETO" no App.css). Onde `py-3` aparece somado
// a um destes é de propósito — é o respiro maior do CTA de largura cheia, e
// vence justamente pelo !important do Bootstrap.
const BTN_SOLIDO =
  "btn-solido inline-flex items-center justify-center gap-2 rounded-controle " +
  "px-6 py-2.5 text-sm font-semibold disabled:opacity-60";
const BTN_SECUNDARIO =
  "inline-flex items-center justify-center gap-2 rounded-controle border border-line " +
  "bg-white/5 px-6 py-2.5 text-sm font-medium text-white transition-colors " +
  "hover:bg-white/10 disabled:opacity-60";

// ===== Bloco de seção escuro (padrão visual da página) =====
const Bloco = ({ titulo, children, className = "" }) => (
  <section className={"bloco p-5 md:p-6 " + className}>
    {titulo && <h3 className="text-xs font-semibold uppercase tracking-wider text-white/45 mb-4">{titulo}</h3>}
    {children}
  </section>
);

const FinalizarCompra = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profileData, updateProfile } = useProfileData();
  const { insigneCoins } = useInsigneCoins();
  const {
    carrinho, valorTotal, getFreteInfo, modificarQuantidade, removerItem, limparCarrinho,
  } = useContext(CarrinhoContext);

  const [usarCoins, setUsarCoins] = useState(false);
  const [cupom, setCupom] = useState("");
  const [cupomMsg, setCupomMsg] = useState("");
  const [editandoEndereco, setEditandoEndereco] = useState(false);
  const [salvandoEndereco, setSalvandoEndereco] = useState(false);
  const [processando, setProcessando] = useState(false);
  const { confirmar, dialogo } = useConfirmarGasto();

  // Quando o POST não devolve resposta (timeout, rede caindo no meio), o pedido
  // PODE ter sido criado: o PHP do servidor termina e comita mesmo com o
  // navegador já desistido. Este estado troca o botão por um aviso com saída
  // explícita — devolver o botão habilitado aqui é literalmente o caminho que
  // produziu #511/#512/#515.
  const [inconclusivo, setInconclusivo] = useState(false);
  // O texto do erro fica guardado para ir PRONTO na conversa com a
  // editora. Sem isto o cliente precisa lembrar e reescrever o que leu,
  // e o que ele reescreve costuma ser "não deu certo" — que não ajuda
  // ninguém a resolver.
  const [ultimoErro, setUltimoErro] = useState("");
  // O servidor cobrou MAIS do que esta tela mostrou: { mostrado, cobrado, dados }
  // em centavos. Enquanto existir, a ida ao pagamento espera a pessoa decidir.
  const [precoMudou, setPrecoMudou] = useState(null);

  // ===== A LINHA SÓ ANDA DEPOIS DO "SIM" DO SERVIDOR =====
  //
  // Onde a pessoa está na jornada. O checkout virou página única, e sem esta
  // trilha não havia nenhuma indicação de que ainda falta uma etapa (o
  // pagamento acontece fora do site, na InfinitePay) — o que fazia o botão
  // "Finalizar" parecer o fim da compra. A peça é a `TrilhaCompra`, a mesma do
  // carrinho e da tela de retorno, e a lista de etapas mora lá (ETAPAS_COMPRA).
  //
  // `avancando` só liga quando o pedido JÁ EXISTE do outro lado. Animar o traço
  // no clique seria prometer uma compra que ainda pode ser recusada por preço,
  // por endereço ou por queda de conexão — e a linha chegando em "Pagamento"
  // enquanto a tela mostra um erro é uma mentira desenhada.
  const [avancando, setAvancando] = useState(false);
  // O que fazer quando o traço chega. Vive numa ref, e não em estado, porque é
  // uma AÇÃO e não algo que se desenha: guardar função em estado faria a página
  // renderizar de novo no meio da animação sem nenhum motivo.
  const irAdiante = useRef(null);

  // Trava SÍNCRONA do envio. `processando` é estado do React: só vale a partir
  // do render seguinte, então `setProcessando(true)` não impede um segundo
  // clique disparado no mesmo tick — o `disabled` do botão ainda não existe
  // quando ele acontece. O ref muda na hora, e é ele que segura de verdade.
  const enviando = useRef(false);

  const freteInfo = getFreteInfo() || {};
  const temFisico = !!freteInfo.temLivrosFisicos;
  const freteValor = temFisico ? freteInfo.freteTotal || 0 : 0;
  const freteDias = freteInfo?.freteInfo?.dias || 20;

  const coinsDisponiveis = insigneCoins?.insignecoins || 0;

  // ===== O QUE AS COINS PODEM PAGAR: SÓ E-BOOK =====
  //
  // Livro impresso e frete ficam DE FORA. A regra é do servidor
  // (`insigne.coins_somente_digital`, aplicada em `_baseParaCoins` no
  // PagarmeController) e esta conta é o ESPELHO dela — não uma segunda regra.
  // O servidor continua sendo quem decide; aqui só se evita mostrar um número
  // que ele vai recusar.
  //
  // O QUE ESTAVA ERRADO, e é o defeito mais caro que um checkout pode ter: o
  // desconto era calculado sobre `valorTotal + freteValor`, ou seja, sobre o
  // carrinho INTEIRO. Quem tinha 500 coins e comprava um livro impresso via
  // "Descontos (500 coins) −R$ 50,00" na revisão, fechava o pedido e era
  // cobrado o valor cheio, porque o servidor não desconta papel nem frete. A
  // tela prometia um preço que a cobrança não cumpria.
  //
  // `formato` ausente conta como e-book — a mesma convenção do servidor
  // (`$item['formato'] ?? 'ebook'`), e a mesma de `criarPedido` logo abaixo.
  const baseParaCoins = useMemo(
    () =>
      carrinho.reduce(
        (soma, item) =>
          (item.formato || "ebook") !== "ebook"
            ? soma
            : soma + (Number(item.preco) || 0) * Math.max(1, Number(item.quantidade) || 1),
        0
      ),
    [carrinho]
  );

  // Quantas coins ESTA compra consegue aproveitar, marcada ou não a opção. O
  // excedente não é queimado: se o saldo cobre mais do que os e-books custam,
  // só sai o necessário. É a mesma conta do servidor — centavos inteiros e
  // arredondamento para cima —, porque o número exibido aqui tem de ser o
  // número debitado lá.
  //
  // Independe de `usarCoins` de propósito: é ele que deixa o rótulo da opção
  // dizer quanto vale marcar ANTES de marcar.
  const coinsAproveitaveis = useMemo(() => {
    if (baseParaCoins <= 0) return 0;
    const baseCentavos = Math.round(baseParaCoins * 100);
    const necessarias = Math.ceil(baseCentavos / COIN_VALOR_CENTAVOS);
    return Math.min(coinsDisponiveis, necessarias);
  }, [baseParaCoins, coinsDisponiveis]);

  // Marcar a opção com o carrinho só de impressos não faz nada, e não precisa
  // de tratamento próprio: `coinsAproveitaveis` já é zero. É o que também
  // cobre o caso de a pessoa marcar a opção e DEPOIS remover o e-book.
  const coinsUsadas = usarCoins ? coinsAproveitaveis : 0;

  // ===== Cálculo do total (espelha o backend criarPedido) =====
  // PIX é à vista: não há acréscimo. O total é arredondado para centavos UMA vez;
  // exibição e payload derivam do mesmo inteiro (totalCentavos).
  const { descontoCoins, total } = useMemo(() => {
    // O teto é a base elegível, não o saldo: com o carrinho só de impressos a
    // base é zero e o desconto é zero, por mais coins que a pessoa tenha.
    const desconto = Math.min((coinsUsadas * COIN_VALOR_CENTAVOS) / 100, baseParaCoins);
    const bruto = valorTotal + freteValor;
    const cent = Math.max(0, Math.round((bruto - desconto) * 100));
    return { descontoCoins: desconto, total: cent / 100 };
  }, [valorTotal, freteValor, coinsUsadas, baseParaCoins]);

  // Complemento entra na conta: sem ele o backend recusa pedido com livro físico.
  const enderecoCompleto = !!(
    profileData?.endereco && profileData?.numero && profileData?.complemento &&
    profileData?.bairro && profileData?.cidade && profileData?.estado && profileData?.cep
  );

  // ===== Não autenticado → tela de Login/Criar Conta (mockup 2) =====
  if (!user) {
    return (
      <div className="bg-insigne-bg min-h-screen pt-[var(--nav-h)] pb-16">
        {/* navBlock + onNext no-op: ao autenticar, permanece no checkout (o user passa a
            ser truthy e o componente re-renderiza na própria página) em vez de ir p/ /perfil. */}
        <LoginOrCreateAccount navBlock onNext={() => {}} />
      </div>
    );
  }

  if (carrinho.length === 0) {
    return (
      <div className="bg-insigne-bg min-h-screen pt-[var(--nav-h)] px-4 text-center text-white">
        <i className="bi bi-cart-x text-5xl text-white/30 mb-4 block" />
        <h2 className="titulo-display text-[2rem]">Seu carrinho está vazio</h2>
        <button onClick={() => navigate(main + "livros")} className={BTN_SOLIDO + " mt-5"}>
          Ver livros
        </button>
      </div>
    );
  }

  // ===== Salvar endereço (reaproveita updateProfile) =====
  const salvarEndereco = async (e) => {
    e.preventDefault();
    const f = e.target;
    const complemento = f.complemento.value.trim();
    if (!complemento) {
      message.warning('Informe o complemento (apto, bloco, casa, sala…). Se não houver, escreva "sem complemento".');
      return;
    }
    try {
      setSalvandoEndereco(true);
      await updateProfile({
        endereco: f.endereco.value, numero: f.numero.value, complemento,
        bairro: f.bairro.value, cidade: f.cidade.value, estado: f.estado.value, cep: f.cep.value,
      });
      message.success("Endereço salvo!");
      setEditandoEndereco(false);
    } catch (err) {
      message.error(err?.message || "Erro ao salvar o endereço.");
    } finally {
      setSalvandoEndereco(false);
    }
  };

  const aplicarCupom = () => {
    // Cupons dependem de endpoint de validação no backend (ainda inexistente).
    if (!cupom.trim()) return;
    setCupomMsg("Cupom inválido ou indisponível no momento.");
  };

  // "Minhas Compras" vive dentro do Perfil (aba meus-pedidos), e a troca de aba
  // é feita por evento porque o Perfil não expõe a aba na URL. Mesma navegação
  // da CompraConcluida — duplicada aqui porque lá ela é uma função interna do
  // componente; extrair para um hook comum é limpeza de outro dia.
  const irParaMinhasCompras = () => {
    navigate(main + "perfil");
    setTimeout(() => window.dispatchEvent(new CustomEvent("changePerfiltab", { detail: "meus-pedidos" })), 150);
  };

  // Devolve o botão ao cliente. Existe como par (ref + estado) porque soltar só
  // um dos dois deixa a tela em estado impossível: botão habilitado que não
  // envia, ou botão travado para sempre.
  const liberarEnvio = () => {
    enviando.current = false;
    setProcessando(false);
  };

  // O que acontece depois que o servidor criou (ou reencontrou) o pedido. É
  // função própria porque tem DUAS entradas: direto, e depois do aviso de que o
  // total subiu, quando a pessoa confirma que quer seguir.
  //
  // `totalCobrado` é o do SERVIDOR sempre que ele o manda. A tela de conclusão
  // do PIX mostrava o total desta página ao lado de um QR Code gerado com o do
  // servidor — dois números na mesma tela quando os dois divergiam.
  //
  // NADA daqui acontece agora: tudo vira a ação que a trilha executa quando o
  // traço chega à etapa "Pagamento". E vai tudo junto, na mesma ordem de
  // sempre, porque esvaziar o carrinho antes derrubaria esta página no "Seu
  // carrinho está vazio" — a trilha sairia da tela no meio da animação, o
  // `aoTerminar` nunca seria chamado, e a navegação, que agora depende dele,
  // nunca aconteceria.
  const seguirComPedido = (dados, totalCobrado) => {
    irAdiante.current = () => {
      // A chave cumpriu o papel. Mantê-la faria uma recompra futura do mesmo
      // carrinho ser respondida com este pedido aqui.
      descartarChave();

      const livrosSnapshot = carrinho.map((i) => ({ ...i }));
      limparCarrinho();

      // Abaixo do mínimo da InfinitePay: PIX da Insigne, na nossa página.
      if (dados.metodo === "pix") {
        navigate(main + "compra-concluida", {
          state: {
            order: dados,
            qrCode: dados.pix?.qr_code,
            pix: dados.pix,
            metodo: "pix",
            total: totalCobrado,
            livros: livrosSnapshot,
            freteDias,
            email: profileData?.email,
          },
        });
        return;
      }

      // As coins cobriram o total: não há gateway, e não há `checkout_url`. Sem
      // esta saída o código cairia no redirect abaixo e mandaria o cliente para
      // "undefined" — e a compra, que no banco já está paga, pareceria ter dado
      // errado. Vai pela URL com `?pedido=`, porque é assim que a tela de
      // conclusão busca o pedido e mostra o status real dele.
      if (!dados.checkout_url) {
        navigate(`${main}compra-concluida?pedido=${dados.pedido_id}`);
        return;
      }

      // ===== Caminho normal: pagar na página da InfinitePay =====
      //
      // O cliente vai DIRETO para o gateway. Ele não passa por WhatsApp,
      // não copia link e não manda mensagem nenhuma: o aviso de venda é
      // trabalho do servidor, disparado quando o pagamento CONFIRMA (ver
      // `WhatsAppNotificacaoService` na API). Quem avisa é quem sabe que
      // o dinheiro entrou — e isso o navegador do comprador nunca sabe.
      if (dados.pedido_reaproveitado) {
        // O toast morre junto com a página no redirect, então avisar e
        // redirecionar no mesmo instante é o mesmo que não avisar. O custo
        // é o cliente ficar ~2 s olhando o checkout já vazio; vale a
        // informação de que o pedido é o mesmo, que é o que evita a
        // terceira tentativa.
        setTimeout(() => { window.location.href = dados.checkout_url; }, 2200);
        return;
      }
      window.location.href = dados.checkout_url;
    };

    // Depois da ação montada, e nunca antes: é este `true` que põe a linha
    // laranja para andar até a etapa "Pagamento".
    setAvancando(true);
  };

  // ===== Finalizar Pedido → checkout da InfinitePay =====
  const finalizarPedido = async () => {
    // Primeira linha, antes de qualquer await: é o único ponto em que a trava
    // ainda chega na frente de um clique duplo.
    if (enviando.current) return;
    enviando.current = true;

    if (temFisico && !enderecoCompleto) {
      message.warning("Cadastre um endereço de entrega completo (com complemento) para os livros físicos.");
      setEditandoEndereco(true);
      liberarEnvio();
      return;
    }

    // O carrinho vive no localStorage e sobrevive a mudanças do catálogo, então
    // pode conter item antigo sem preço válido. Antes isso virava um 422 seco
    // ("Dados inválidos") sem dizer qual livro era o problema.
    const invalidos = carrinho.filter((i) => {
      const p = parseFloat(i.preco);
      const q = parseInt(i.quantidade, 10);
      return !i.id || isNaN(p) || p <= 0 || isNaN(q) || q < 1;
    });

    if (invalidos.length > 0) {
      const nomes = invalidos.map((i) => i.titulo || `livro ${i.id}`).join(", ");
      message.error(
        `Estes itens do carrinho estão desatualizados e precisam ser removidos: ${nomes}. ` +
        `Abra o carrinho, remova-os e adicione novamente.`,
        8
      );
      liberarEnvio();
      return;
    }

    const orderData = {
      usuario_editora_id: parseInt(user, 10),
      livros: carrinho.map((item) => ({
        id: parseInt(item.id, 10),
        preco: parseFloat(item.preco),
        quantidade: parseInt(item.quantidade, 10),
        formato: item.formato || "ebook",
      })),
      // O que a compra CONSOME, e não o saldo inteiro. O servidor já debitava
      // só o necessário (`min(coinsToUse, coinsNecessarias)`), então mandar o
      // saldo cheio nunca queimou moeda — mas mandava um número que não era o
      // da tela, e é essa divergência que faz o log de pagamento contar uma
      // história diferente da que o cliente viu.
      insigneCoins: coinsUsadas,
      // A MODALIDADE do frete, e não o valor: o servidor tem a tabela (config
      // `insigne.frete`) e cobra por ela. Até 11/09/2026 nada disto ia no
      // pedido, e o servidor cobrava R$ 20 fixos mesmo com o expresso de R$ 70
      // somado no total desta tela.
      frete_modalidade: freteInfo?.freteInfo?.chave || "padrao",
      // A mesma chave em toda tentativa deste carrinho. É o que faz o servidor
      // devolver o pedido que já existe em vez de criar outro quando a segunda
      // tentativa é uma REQUISIÇÃO de verdade — F5, retentativa depois de um
      // timeout, cliente voltando do gateway. Nenhuma trava de botão cobre isso.
      chave_idempotencia: chaveParaCarrinho(carrinho),
    };

    // O backend identifica o dono do pedido pelo token, não pelo id do corpo:
    // sem este cabeçalho a rota responde 401.
    const token = localStorage.getItem("token" + (process.env.REACT_APP_USER_FUNCTION || ""));
    if (!token) {
      message.error("Sua sessão expirou. Entre novamente para finalizar a compra.");
      navigate(main + "login");
      liberarEnvio();
      return;
    }

    // ===== COINS NA COMPRA: CONFIRMA ANTES =====
    // Pedido do dono (11/09/2026): todo gasto de Coins passa por uma caixa de
    // confirmação (ui/ConfirmarGasto). Aqui é o último ponto antes de o pedido
    // nascer e as Coins serem debitadas — e, quando elas cobrem o total, não
    // existe tela de pagamento depois para a pessoa desistir. A trava
    // `enviando` continua ligada enquanto a caixa está aberta: um segundo
    // clique no botão não abre outra por baixo.
    if (coinsUsadas > 0) {
      const ok = await confirmar({
        tom: "coin",
        titulo: "Usar Coins nesta compra",
        linhas: [
          { rotulo: "Você usa", valor: <Coins n={coinsUsadas} /> },
          { rotulo: "Desconto", valor: <span className="font-semibold text-insigne-promo">- {fmtBRL(descontoCoins)}</span> },
          { rotulo: "Total a pagar", valor: <strong>{fmtBRL(total)}</strong> },
          { rotulo: "Suas Coins", valor: <>{coinsDisponiveis} → <Coins n={Math.max(0, coinsDisponiveis - coinsUsadas)} /></> },
        ],
        nota: total <= 0
          ? "As Coins cobrem o pedido inteiro: ele é confirmado na hora, sem passar pelo pagamento."
          : "O restante você paga na próxima tela.",
        textoConfirmar: total <= 0 ? "Confirmar compra" : "Confirmar e pagar",
      });
      if (!ok) {
        liberarEnvio();
        return;
      }
    }

    try {
      setProcessando(true);
      const resp = await axios.post(`${API_URL}infinitepay/pedido`, orderData, {
        headers: { Authorization: "Bearer " + token },
        // Acima dos 20 s que o Guzzle do servidor usa para falar com a
        // InfinitePay, DE PROPÓSITO: assim, quando o navegador desiste, o lado
        // de lá já terminou de um jeito ou de outro e o modo de falha é um só.
        // Um timeout menor que o do servidor produz a pior combinação possível
        // — o cliente vê erro enquanto o pedido nasce. Sem timeout nenhum, que
        // era o caso, a espera é a do sistema operacional (minutos), e é aí que
        // o cliente clica de novo.
        timeout: 45000,
      });
      // 201 = pedido novo. 200 = o servidor reconheceu a chave (ou achou um
      // pedido pendente idêntico dos últimos minutos) e devolveu o que já
      // existia. Exigir 201, como antes, transformava o reaproveitamento em
      // "Resposta inesperada do servidor" — o erro que faz o cliente clicar
      // mais uma vez, com o carrinho intacto e o pedido já criado do outro lado.
      if ((resp.status !== 200 && resp.status !== 201) || !resp.data?.pedido_id) {
        throw new Error(resp.data?.message || "Resposta inesperada do servidor.");
      }

      // O pedido já existia. Dizer isso ANTES de seguir, senão a tentativa que
      // o cliente acabou de fazer parece ter sumido e ele tenta uma terceira.
      if (resp.data.pedido_reaproveitado) {
        message.info(
          `Este pedido já tinha sido criado (nº ${resp.data.pedido_id}). ` +
          "Não geramos um novo: você está indo para o pagamento do mesmo pedido.",
          8
        );
      }

      // ===== O SERVIDOR COBROU MAIS DO QUE ESTA TELA MOSTROU? =====
      //
      // O carrinho guarda o preço do momento em que o livro entrou nele, e o
      // servidor cobra sempre o preço de AGORA. Todo livro da vitrine de
      // destaques tem desconto, e quem sai dela perde — o que acontece sozinho
      // quando um título novo é publicado e empurra o último para fora. Quem
      // tinha esse livro no carrinho a R$ 27,00 era cobrado R$ 30,00 e só
      // descobria na página do gateway, sem uma palavra daqui.
      //
      // O servidor já devolvia o total que cobrou (`valor_total_centavos`); só
      // faltava alguém olhar. Só a SUBIDA para: cobrar menos do que se mostrou
      // não prejudica ninguém, e o frete desta tela nunca fica abaixo do que o
      // servidor calcula, então ele não dispara o aviso sozinho.
      //
      // A chave de idempotência NÃO é descartada aqui. Se a pessoa voltar ao
      // carrinho e tentar de novo sem mexer nele, o servidor devolve este mesmo
      // pedido, em vez de criar outro.
      const cobrado = Number(resp.data.valor_total_centavos);
      const mostrado = Math.round(total * 100);
      if (Number.isFinite(cobrado) && cobrado > mostrado + 1) {
        setPrecoMudou({ mostrado, cobrado, dados: resp.data });
        // `enviando` continua travado enquanto o aviso está aberto: o botão
        // de trás não pode criar uma segunda tentativa por baixo dele.
        setProcessando(false);
        return;
      }

      seguirComPedido(resp.data, Number.isFinite(cobrado) ? cobrado / 100 : total);
    } catch (err) {
      // Token expirado no meio da sessão: manda para o login em vez de mostrar
      // "Não autenticado", que não diz nada ao cliente.
      if (err.response?.status === 401) {
        message.error("Sua sessão expirou. Entre novamente para finalizar a compra.");
        navigate(main + "login");
        liberarEnvio();
        return;
      }

      // ===== A separação que importa: houve resposta HTTP ou não? =====
      //
      // COM `err.response` (403, 422, 500) o servidor falou: a requisição
      // chegou, foi recusada e nada foi criado. Mostrar o erro e devolver o
      // botão é o certo — o cliente corrige e tenta de novo.
      //
      // SEM `err.response` (timeout de 45 s, rede caindo, aba suspensa) o
      // navegador não sabe o que houve do outro lado. O pedido pode existir, e
      // pode até estar pago. Aqui o botão NÃO volta: ele vira um aviso com o
      // caminho para conferir. É a diferença entre um cliente confuso e nove
      // livros. A chave de idempotência também não é descartada — é ela que
      // protege a próxima tentativa, depois que a pessoa recarregar.
      if (!err.response) {
        setUltimoErro("A resposta do servidor não chegou (possível queda de conexão).");
        setInconclusivo(true);
        setProcessando(false); // `enviando` continua travado de propósito
        return;
      }

      const bruto = err.response?.data?.message || err.response?.data?.error || err.message || "";
      // Preço do carrinho diferente do preço atual do livro: sozinha, a mensagem
      // do backend não diz ao cliente o que fazer.
      const texto = bruto.includes("não confere")
        ? bruto + " Remova o livro do carrinho e adicione novamente para pegar o preço atual."
        : bruto || "Erro ao gerar o pagamento.";
      setUltimoErro(texto);
      message.error(texto, 8);
      liberarEnvio();
    }
  };

  // ===== Render =====
  // Era a quarta cópia do campo de formulário do site, e a única com medidas
  // próprias: `px-3.5 py-2.5` contra `px-4 py-3` das outras três. Ou seja, os
  // campos onde se digita o endereço de entrega eram menores que os do perfil.
  const inputCls = campoCls;

  return (
    <div className="bg-insigne-bg fundo-abertura min-h-screen pt-[var(--nav-h)] px-4 md:px-8 pb-16 text-white">
      <div className="max-w-[1180px] mx-auto">
        {/* A trilha SOZINHA, sem caixa e sem título em cima. O <h1>
            "Finalizando a Compra" repetia em letra grande o que ela já mostra
            em desenho, e o `bloco` azul em volta transformava a indicação de
            percurso numa faixa pintada competindo com o resumo do pedido. */}
        <TrilhaCompra
          passos={ETAPAS_COMPRA}
          atual={1}
          avancando={avancando}
          // Uma ida só: a ação sai da ref ANTES de rodar. Hoje a trilha avisa
          // o fim do traço por um relógio só (o `transitionend` não chega com
          // ela fora da tela — ver TrilhaCompra); a guarda existe para que um
          // segundo gatilho, se um dia for ligado lá, não vire duas navegações.
          aoTerminar={() => {
            const ir = irAdiante.current;
            irAdiante.current = null;
            ir?.();
          }}
          className="max-w-[720px] mx-auto mt-6 mb-8"
        />

        <div className="flex flex-col lg:flex-row gap-6 items-start">
          {/* ===== Coluna esquerda ===== */}
          <div className="flex-1 min-w-0 w-full space-y-5">
            {/* Endereço de entrega */}
            <Bloco titulo="Endereço de Entrega">
              {editandoEndereco ? (
                <form onSubmit={salvarEndereco} className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_140px] gap-3">
                    <input name="endereco" defaultValue={profileData?.endereco || ""} placeholder="Endereço" className={inputCls} required />
                    <input name="numero" defaultValue={profileData?.numero || ""} placeholder="Nº" className={inputCls} required />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input name="bairro" defaultValue={profileData?.bairro || ""} placeholder="Bairro" className={inputCls} required />
                    <div>
                      <input name="complemento" defaultValue={profileData?.complemento || ""} placeholder="Complemento (obrigatório)" className={inputCls} required />
                      <p className="text-xs text-white/40 mt-1">Apto, bloco, casa, sala… Se não houver, escreva "sem complemento".</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_120px_140px] gap-3">
                    <input name="cidade" defaultValue={profileData?.cidade || ""} placeholder="Cidade" className={inputCls} required />
                    <input name="estado" defaultValue={profileData?.estado || ""} placeholder="UF" maxLength={2} className={inputCls} required />
                    <input name="cep" defaultValue={profileData?.cep || ""} placeholder="CEP" className={inputCls} required />
                  </div>
                  <div className="flex flex-wrap gap-3 pt-1">
                    <button type="submit" disabled={salvandoEndereco} className={BTN_SOLIDO}>
                      {salvandoEndereco ? "Salvando..." : "Salvar"}
                    </button>
                    <button type="button" onClick={() => setEditandoEndereco(false)} className={BTN_SECUNDARIO}>Cancelar</button>
                  </div>
                </form>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-4">
                    <p className="text-white/90 text-sm leading-relaxed">
                      {enderecoCompleto
                        ? `${profileData.endereco}, Nº ${profileData.numero} - ${profileData.complemento}. ${profileData.bairro}, ${profileData.cidade}/${profileData.estado} - ${profileData.cep}`
                        : "Nenhum endereço completo cadastrado."}
                    </p>
                    {enderecoCompleto && (
                      <button onClick={() => setEditandoEndereco(true)} className="text-insigne-orange text-sm font-medium hover:underline shrink-0">Alterar Endereço</button>
                    )}
                  </div>
                  <button onClick={() => setEditandoEndereco(true)} className={BTN_SECUNDARIO + " mt-4"}>
                    <i className="bi bi-geo-alt" /> {enderecoCompleto ? "Cadastrar Novo Endereço" : "Cadastrar Endereço"}
                  </button>
                  {temFisico && !enderecoCompleto && (
                    <p className="text-xs text-amber-300 mt-3">
                      Seu pedido tem livro físico: preencha o endereço completo, incluindo o complemento.
                    </p>
                  )}
                  {!temFisico && <p className="text-xs text-white/40 mt-3">Pedido apenas com e-books: entrega digital, endereço opcional.</p>}
                </>
              )}
            </Bloco>

            {/* Pagamento — acontece na página segura da InfinitePay */}
            <Bloco titulo="Pagamento">
              <div className="flex items-start gap-3 py-2">
                <i className="bi bi-shield-lock text-white/70 text-lg mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-medium">Pagamento seguro pela InfinitePay</p>
                  <p className="text-sm text-white/60 mt-1">
                    Ao finalizar, você vai para a página de pagamento da InfinitePay, onde
                    escolhe <strong className="text-white/80">PIX</strong> (aprovação na hora)
                    ou cartão de crédito. Depois você volta para cá automaticamente.
                  </p>
                  <p className="text-xs text-white/40 mt-2">
                    Assim que o pagamento é confirmado, seus e-books ficam disponíveis no perfil
                    e você recebe um e-mail, sem espera.
                  </p>
                </div>
              </div>
            </Bloco>

            {/* Produtos */}
            <Bloco titulo={`Produtos (${carrinho.length})`}>
              <div className="divide-y divide-line">
                {carrinho.map((item) => (
                  <div key={`${item.id}-${item.formato}`} className="flex gap-4 py-4 first:pt-0 last:pb-0">
                    <div className="w-14 h-20 rounded-card overflow-hidden bg-insigne-navy-deep shrink-0 flex items-center justify-center">
                      {capaUrl(item) ? <img src={capaUrl(item)} alt={item.titulo} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} /> : <i className="bi bi-book text-white/30" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between gap-3">
                        <h4 className="font-semibold text-sm leading-tight">{item.titulo || "Produto"}</h4>
                        {/* O "de" riscado tambem no resumo do pedido: e a
                            ultima tela antes do pagamento, e e onde a pessoa
                            confere se o desconto que a fez clicar continua ali.
                            Sem isto, o desconto aparecia no card e sumia na
                            hora de pagar -- que e onde a desconfianca custa a
                            venda. `preco_original` viaja com o item; quem foi
                            salvo antes nao tem o campo e a linha sai sem o
                            riscado. */}
                        <p className="whitespace-nowrap text-sm text-right">
                          {parseFloat(item.preco_original) > parseFloat(item.preco) && (
                            <span className="block text-[10px] text-white/35 line-through leading-none">
                              R$ {fmtPreco(item.preco_original)}
                            </span>
                          )}
                          <span className="text-[10px] align-top">R$</span> <span className="font-bold">{fmtPreco(item.preco)}</span>
                        </p>
                      </div>
                      {autorDe(item) && <p className="text-white/55 text-xs mt-0.5">{autorDe(item)}</p>}
                      <p className="text-white/45 text-xs mt-1">Previsão de Entrega: {item.formato === "fisico" ? previsaoEntrega(new Date(), freteDias) : "Imediata (e-book)"}</p>
                      <div className="mt-2 inline-flex items-center rounded-controle border border-line overflow-hidden">
                        <button onClick={() => (item.quantidade <= 1 ? removerItem(item.id, item.formato) : modificarQuantidade(item.id, item.formato, item.quantidade - 1))} className="w-8 h-8 flex items-center justify-center text-white/70 hover:bg-white/5"><i className={"bi " + (item.quantidade <= 1 ? "bi-trash text-red-400" : "bi-dash-lg")} /></button>
                        <span className="min-w-8 text-center text-sm font-semibold">{item.quantidade}</span>
                        <button onClick={() => item.formato === "fisico" && modificarQuantidade(item.id, item.formato, item.quantidade + 1)} disabled={item.formato !== "fisico"} className="w-8 h-8 flex items-center justify-center text-insigne-orange hover:bg-white/5 disabled:opacity-30"><i className="bi bi-plus-lg" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Bloco>
          </div>

          {/* ===== Coluna direita: resumo ===== */}
          <aside className="w-full lg:w-[360px] shrink-0 lg:sticky lg:top-[150px] space-y-4">
            <div className="bloco p-6">
              {/* ===== Cupom e Coins: dois irmãos, a mesma forma =====
                  As duas caixas respondem à mesma pergunta ("tenho alguma coisa
                  que abate este total?"), e por isso recebem o mesmo desenho:
                  rebaixo em `navy-deep`, rótulo curto em cima, controle embaixo.
                  Antes eram dois experimentos soltos DEPOIS do botão de pagar —
                  um com campo e botão, o outro com caixa de marcar — e ninguém
                  lia os dois como a mesma família.
                  REBAIXO, e não cartão: relevo aqui faria os dois brigarem com
                  o botão de pagar, que é o único destino desta coluna. E vêm
                  ANTES do total porque é o total que eles mudam. */}
              <div className="space-y-2.5">
                {/* Cupom (stub visual) */}
                <div className="rounded-card border border-line bg-insigne-navy-deep p-3.5">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-white/45 mb-2">Cupom</p>
                  <div className="flex items-center gap-2">
                    {/* `.campo-foco` desenha o anel laranja de 3 px no
                        `:focus-within`. Vai no wrapper, e não no input, porque é
                        ele que contém o ícone; o raio aqui existe só para o anel
                        seguir o mesmo contorno do campo. */}
                    <div className="relative flex-1 min-w-0 rounded-controle campo-foco">
                      <i className="bi bi-tag absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm" />
                      {/* Único campo da tela em `bg-insigne-bg` no lugar do
                          `navy-deep` de `campoCls`: este mora DENTRO de um
                          rebaixo que já é navy-deep, e campo da mesma cor do
                          poço desaparece — a hairline do tema escuro é mais
                          escura que o card de propósito e não salva o contorno
                          sozinha. */}
                      <input value={cupom} onChange={(e) => { setCupom(e.target.value); setCupomMsg(""); }} placeholder="Insira um Cupom" className="w-full rounded-controle bg-insigne-bg border border-line pl-9 pr-3 py-2.5 text-sm text-white placeholder:text-white/40 outline-none focus:border-insigne-navy-light" />
                    </div>
                    <button onClick={aplicarCupom} className={BTN_SECUNDARIO + " shrink-0"}>Aplicar</button>
                  </div>
                  {cupomMsg && <p className="text-xs text-amber-300 mt-2">{cupomMsg}</p>}
                </div>

                {/* ===== Insigne Coins como desconto =====
                    A opção só aparece quando há o que descontar. Com o carrinho
                    só de impressos, no lugar dela entra a EXPLICAÇÃO: uma caixa
                    marcável que não muda o total é pior do que caixa nenhuma —
                    a pessoa marca, o preço não se mexe, e ela conclui que o site
                    comeu as coins dela. */}
                {coinsDisponiveis > 0 && (
                  <div className="rounded-card border border-line bg-insigne-navy-deep p-3.5">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-white/45 mb-2">Insigne Coins</p>
                    {coinsAproveitaveis > 0 ? (
                      <label className="flex items-start gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={usarCoins} onChange={(e) => setUsarCoins(e.target.checked)} className="mt-0.5 w-4 h-4 shrink-0 accent-insigne-orange" />
                        <span className="text-white/80">
                          Usar {coinsAproveitaveis} Insigne Coins ({fmtBRL((coinsAproveitaveis * COIN_VALOR_CENTAVOS) / 100)})
                          {/* Quando o saldo é maior que os e-books do carrinho, o
                              resto FICA. Dizer isso aqui evita a pergunta que
                              chegaria ao suporte: "usei 500 e só saíram 120, cadê
                              o resto?" */}
                          {coinsAproveitaveis < coinsDisponiveis && (
                            <span className="block text-xs text-white/45 mt-0.5">
                              Você tem {coinsDisponiveis}; o resto continua no seu saldo.
                            </span>
                          )}
                        </span>
                      </label>
                    ) : (
                      <p className="flex items-start gap-2 text-xs text-white/45">
                        <i className="bi bi-info-circle mt-0.5 shrink-0" />
                        <span>
                          Seus {coinsDisponiveis} Insigne Coins valem desconto em
                          e-books e no Clube, não em livro impresso nem no frete.
                        </span>
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* ===== O total =====
                  A mesma hierarquia do carrinho: as parcelas em corpo pequeno e
                  cinza, uma divisória, e o TOTAL como a única linha grande da
                  coluna. É o número que a pessoa leva para a página do gateway,
                  e é o único que ela vai conferir lá. */}
              <div className="mt-5 space-y-2 text-sm">
                <Row label="Subtotal" value={fmtBRL(valorTotal)} />
                {freteValor > 0 && <Row label="Frete" value={fmtBRL(freteValor)} />}
                {descontoCoins > 0 && <Row label={`Descontos (${coinsUsadas} coins)`} value={"-" + fmtBRL(descontoCoins)} green />}
              </div>
              {/* A divisória é `white/10`, e não `border-line`: a hairline do
                  tema escuro é mais escura que o card e sumiria dentro dele. */}
              <div className="h-px bg-white/10 my-4" />
              <div className="flex items-end justify-between gap-3">
                <span className="font-semibold text-white">Total</span>
                <span className="whitespace-nowrap"><span className="text-xs align-top">R$</span> <span className="text-2xl font-bold">{fmtBRL(total).replace("R$ ", "")}</span></span>
              </div>

              {/* O envio ficou sem resposta: o botão dá lugar ao aviso. Trocar o
                  bloco inteiro, em vez de só desabilitar, é o que impede a tela de
                  virar beco sem saída — quem chega aqui precisa de um lugar para
                  ir, e o lugar é a lista de pedidos. */}
              {inconclusivo ? (
                <div className="mt-5 rounded-card border border-amber-400/40 bg-amber-400/10 p-4">
                  <p className="flex items-start gap-2 text-sm font-semibold text-amber-200">
                    <i className="bi bi-exclamation-triangle-fill mt-0.5 shrink-0" />
                    Não conseguimos confirmar se o pedido foi criado.
                  </p>
                  <p className="mt-2 text-sm text-white/75">
                    A resposta do servidor não chegou até você, mas o pedido pode ter sido
                    criado mesmo assim. Veja em Minhas Compras antes de tentar de novo:
                    tentar agora é o que faz nascer um pedido repetido.
                  </p>
                  <button onClick={irParaMinhasCompras} className={BTN_SOLIDO + " mt-3 w-full py-3 text-base"}>
                    Ver Minhas Compras
                  </button>
                  {/* Recarregar é a única forma de destravar, e é segura: o carrinho
                      continua salvo e a chave de idempotência sobrevive ao F5, então
                      uma nova tentativa cai no MESMO pedido, se ele existir. */}
                  <button onClick={() => window.location.reload()} className={BTN_SECUNDARIO + " mt-2 w-full"}>
                    Não está lá? Recarregar e tentar de novo
                  </button>
                  <a
                    href={linkSocorroWhatsApp({
                      erro: ultimoErro,
                      etapa: "ao gerar o pagamento",
                      total,
                      nome: profileData?.nome,
                    })}
                    target="_blank" rel="noopener noreferrer"
                    className="mt-2 flex w-full items-center justify-center gap-2 rounded-controle border border-emerald-400/40 bg-emerald-400/10 py-2.5 text-sm font-medium text-emerald-200 hover:bg-emerald-400/20"
                  >
                    <i className="bi bi-whatsapp" />
                    Falar com a editora no WhatsApp
                  </a>
                </div>
              ) : (
                /* Partículas ao clicar: é o único botão da loja que fecha um
                   pedido, e vale marcar o momento. Elas só saem quando o clique
                   vale — com `processando` ativo não há comemoração.
                   `py-3` e `text-base` somados ao BTN_SOLIDO são de propósito: é
                   o respiro maior que o CTA de largura cheia pede (o `py-3` do
                   Bootstrap vem com !important e vence o `py-2.5` da receita). */
                <BotaoParticulas onClick={finalizarPedido} disabled={processando} className={BTN_SOLIDO + " mt-5 w-full py-3 text-base"}>
                  <i className="bi bi-lock-fill" />
                  {processando ? "Redirecionando..." : "Ir para o Pagamento"}
                </BotaoParticulas>
              )}

              {/* O erro que o servidor RECUSOU (422, 403, 500) some junto com o
                  toast em 8 segundos, e com ele some a única pista que o cliente
                  tinha. Este bloco fica: repete o motivo e oferece a saída com a
                  mensagem já escrita. Sem ele, quem não consegue pagar fecha a
                  aba — e a venda morre sem ninguém saber que existiu. */}
              {!inconclusivo && ultimoErro && (
                <div className="mt-3 rounded-card border border-red-400/30 bg-red-400/10 p-3">
                  <p className="text-sm text-red-200">{ultimoErro}</p>
                  <a
                    href={linkSocorroWhatsApp({
                      erro: ultimoErro,
                      etapa: "ao gerar o pagamento",
                      total,
                      nome: profileData?.nome,
                    })}
                    target="_blank" rel="noopener noreferrer"
                    className="mt-2 flex w-full items-center justify-center gap-2 rounded-controle border border-emerald-400/40 bg-emerald-400/10 py-2 text-sm font-medium text-emerald-200 hover:bg-emerald-400/20"
                  >
                    <i className="bi bi-whatsapp" />
                    Resolver pelo WhatsApp
                  </a>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>

      {/* ===== O total subiu: a pessoa decide antes de ir ao pagamento =====
          Ver o comentário em `finalizarPedido`. Diálogo, e não aviso que some:
          um toast morre junto com a página no redirect para o gateway, e
          dinheiro a mais precisa de um "sim" de quem vai pagar. */}
      {dialogo}

      {precoMudou && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 px-4">
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="preco-mudou-titulo"
            aria-describedby="preco-mudou-texto"
            className="w-full max-w-md bloco p-6 text-white shadow-2xl"
          >
            <h2 id="preco-mudou-titulo" className="text-lg font-bold">O total do pedido mudou</h2>
            <p id="preco-mudou-texto" className="mt-2 text-sm text-white/70">
              O motivo mais comum: um livro do seu carrinho saiu da promoção depois
              que você o adicionou. O pedido é sempre cobrado pelo preço de agora.
            </p>
            <p className="mt-4 flex flex-wrap items-baseline gap-x-3">
              <span className="text-white/45 line-through">
                <span className="sr-only">Antes: </span>{fmtBRL(precoMudou.mostrado / 100)}
              </span>
              <span className="text-2xl font-bold">
                <span className="sr-only">Agora: </span>{fmtBRL(precoMudou.cobrado / 100)}
              </span>
            </p>
            <div className="mt-6 flex flex-col gap-2.5">
              <button
                type="button"
                autoFocus
                onClick={() => {
                  const { dados, cobrado } = precoMudou;
                  setPrecoMudou(null);
                  setProcessando(true);
                  seguirComPedido(dados, cobrado / 100);
                }}
                className={BTN_SOLIDO + " w-full py-3 text-base"}
              >
                <i className="bi bi-lock-fill" /> Continuar para o pagamento
              </button>
              <button
                type="button"
                onClick={() => {
                  setPrecoMudou(null);
                  liberarEnvio();
                  navigate(main + "carrinho");
                }}
                className={BTN_SECUNDARIO + " w-full"}
              >
                Voltar ao carrinho
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Linha do resumo
const Row = ({ label, value, muted, green }) => (
  <div className="flex items-center justify-between">
    <span className={muted ? "text-white/50" : "text-white/70"}>{label}</span>
    <span className={green ? "text-insigne-promo" : muted ? "text-white/60" : "text-white"}>{value}</span>
  </div>
);

export default FinalizarCompra;
